import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { isNil } from '@trimble-gcs/common';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { TranslationService } from '../../../../../../libs/i18n/src';
import { ImportFile, ImportFileStatus } from '../import.models';
import { importFileListTranslations } from './import-file-list.translations';

interface ImportFileView {
  id: string;
  name: string;
  size: number;

  statusMessage?: string;
  status?: ImportFileStatus;
  progress?: number;
  progressTooltip: string;
}

@Component({
  selector: 'sd-import-file-list',
  imports: [
    CommonModule,
    ModusButtonModule,
    ModusTooltipModule,
    ModusIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './import-file-list.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex: 1 1 auto;
        overflow: hidden;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportFileListComponent {
  private readonly translationService = inject(TranslationService);
  private readonly currentFile = viewChild<ElementRef<HTMLDivElement>>('currentFile');

  readonly importFiles = input.required<ImportFile[]>();
  readonly disableRemove = input.required<boolean>();

  readonly removeFiles = output<ImportFile[]>();

  readonly hasImportFiles = computed(() => this.importFiles().length > 0);
  readonly importFilesView = computed(() => this.mapImportFileView(this.importFiles()));

  readonly importFileStatus = ImportFileStatus;
  readonly translations = importFileListTranslations();

  constructor() {
    effect(() => {
      const currentFile = this.currentFile();
      if (isNil(currentFile)) return;

      currentFile.nativeElement.scrollIntoView({ behavior: 'smooth' });
    });
  }

  removeFile(importFileView: ImportFileView) {
    const importFile = this.importFiles().find((file) => file.id === importFileView.id)!;
    this.removeFiles.emit([importFile]);
  }

  private mapImportFileView(importFiles: ImportFile[]) {
    return importFiles.map((importFile) => {
      const progress = importFile.fileUpload?.progress;

      const progressTooltip = isNil(progress)
        ? this.translations.status.pending.text
        : this.translationService.translate(this.translations.status.progress.text, {
            percentageComplete: progress,
          });

      return {
        ...importFile,
        progress,
        progressTooltip,
      } satisfies ImportFileView;
    });
  }
}
